"use client";

import { type RefObject, useEffect } from "react";

/**
 * When MathLive's virtual keyboard opens, appends a spacer div
 * (matching the keyboard height) inside the content container
 * (<main> / SidebarInset) so users can scroll to see all content
 * behind the keyboard.
 *
 * The spacer must go inside <main> (not as a sibling) because
 * <main> has h-full which fills the scroll container — a sibling
 * spacer wouldn't extend scrollHeight.
 */
export function useMathVirtualKeyboardPadding(
  scrollContainerRef: RefObject<HTMLElement | null>,
) {
  useEffect(() => {
    const mvk = (window as any).mathVirtualKeyboard;
    if (!mvk) return;

    const getContentContainer = () => {
      const el = scrollContainerRef.current;
      if (!el) return null;
      // SidebarInset renders as <main>, the first child of the scroll container
      const main = el.querySelector("main") ?? el.firstElementChild;
      return (main as HTMLElement) ?? el;
    };

    const update = () => {
      requestAnimationFrame(() => {
        const container = getContentContainer();
        if (!container) return;

        const rect = mvk.boundingRect as DOMRect | undefined;
        const height = rect?.height ?? 0;

        let spacer = container.querySelector<HTMLElement>(
          "[data-keyboard-spacer]",
        );
        if (height > 0) {
          if (!spacer) {
            spacer = document.createElement("div");
            spacer.setAttribute("data-keyboard-spacer", "");
            spacer.style.flexShrink = "0";
            container.appendChild(spacer);
          }
          spacer.style.height = `${height}px`;
        } else if (spacer) {
          spacer.remove();
        }

        // Scroll the focused math-field into view above the keyboard
        if (height > 0) {
          const active = document.activeElement;
          if (active?.tagName?.toLowerCase() === "math-field") {
            active.scrollIntoView({ behavior: "smooth", block: "nearest" });
          }
        }
      });
    };

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && mvk.visible) {
        mvk.hide();
      }
    };

    mvk.addEventListener("geometrychange", update);
    mvk.addEventListener("virtual-keyboard-toggle", update);
    document.addEventListener("keydown", handleEscape);

    return () => {
      mvk.removeEventListener("geometrychange", update);
      mvk.removeEventListener("virtual-keyboard-toggle", update);
      document.removeEventListener("keydown", handleEscape);
      const container = getContentContainer();
      if (container) {
        const spacer = container.querySelector("[data-keyboard-spacer]");
        if (spacer) spacer.remove();
      }
    };
  }, [scrollContainerRef]);
}
